import { useCallback, useEffect } from 'react';

interface UsePasteUploadOptions {
    /** Only listen for paste events while enabled (e.g. while a modal is open). */
    enabled: boolean;
    onFilesPasted: (files: File[]) => void;
}

/**
 * Collect files from a clipboard paste (Ctrl/Cmd + V).
 *
 * Reads files from both `clipboardData.files` and `clipboardData.items`
 * (the latter is how pasted images from a screenshot/clipboard usually arrive).
 * Mirrors the style of {@link useDragDrop}.
 */
export function usePasteUpload({ enabled, onFilesPasted }: UsePasteUploadOptions): void {
    const handlePaste = useCallback((e: ClipboardEvent) => {
        const data = e.clipboardData;
        if (!data) return;

        const files: File[] = [];
        // The same paste appears in BOTH `data.files` and `data.items` as distinct
        // File objects, so dedupe by content signature rather than identity.
        // Use only name + size: a pasted screenshot is a synthesized File whose
        // `lastModified` is stamped with the current time independently for each
        // source, so including it would differ by a few ms and fail to dedupe,
        // producing two identical "image.png". Real OS files share name + size
        // across both sources too, so this dedupes them as well.
        const seen = new Set<string>();
        const add = (file: File | null) => {
            if (!file) return;
            const signature = `${file.name}|${file.size}`;
            if (seen.has(signature)) return;
            seen.add(signature);
            files.push(file);
        };

        // Directly attached files (e.g. a file copied in the OS file manager).
        for (let i = 0; i < data.files.length; i++) {
            add(data.files.item(i));
        }

        // Items — pasted images (screenshots, copied image data) live here.
        if (data.items) {
            for (let i = 0; i < data.items.length; i++) {
                const item = data.items[i];
                if (item.kind === 'file') {
                    add(item.getAsFile());
                }
            }
        }

        if (files.length > 0) {
            e.preventDefault();
            onFilesPasted(files);
        }
    }, [onFilesPasted]);

    useEffect(() => {
        if (!enabled) return;
        // Attach to `document` — clipboard events are typed there (not on Window).
        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [enabled, handlePaste]);
}
